/**
 * DataLayer utility for Google Analytics / Google Tag Manager
 * Provides type-safe methods to push events to the dataLayer
 */

// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Common event types
export interface DataLayerEvent {
  event: string;
  [key: string]: any;
}

export interface PageViewEvent {
  event: "page_view";
  region: string;
  session_context: string;
  user_role: string;
  page_title: string;
  page_location: string;
  page_path: string;
}

export interface UserEvent {
  event: "user_event";
  event_category: string;
  event_action: string;
  event_label?: string;
  event_value?: number;
}

export interface CustomEvent {
  event: string;
  event_category?: string;
  event_action?: string;
  event_label?: string;
  [key: string]: any;
}

/**
 * Initialize dataLayer if it doesn't exist
 */
const initDataLayer = (): void => {
  if (typeof window !== "undefined" && !window.dataLayer) {
    window.dataLayer = [];
  }
};

/**
 * Push data to the dataLayer
 * @param data - The data object to push
 */
export const pushToDataLayer = (data: DataLayerEvent): void => {
  // console.log("dataLayer", data);
  if (typeof window === "undefined") {
    console.warn("DataLayer: Window is not defined (SSR environment)");
    return;
  }

  initDataLayer();

  try {
    window.dataLayer.push(data);
  } catch (error) {
    console.error("Error pushing to dataLayer:", error);
  }
};

/**
 * Track a page view
 * @param title - Page title
 * @param path - Page path
 * @param location - Full page location (optional)
 */
export const trackPageView = (title: string, path: string, location?: string): void => {
  const pageViewData: PageViewEvent = {
    event: "page_view",
    region: JSON.parse(localStorage.getItem("location") || "{}").region,
    session_context: "web",
    user_role: localStorage.getItem("role") || "visitor",
    page_title: title,
    page_location: location || window.location.href,
    page_path: path,
  };

  pushToDataLayer(pageViewData);
};

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param eventData - Additional event data
 */
export const trackEvent = (eventName: string, eventData?: Record<string, any>): void => {
  pushToDataLayer({
    event: eventName,
    region: JSON.parse(localStorage.getItem("location") || "{}").region,
    session_context: "web",
    user_role: localStorage.getItem("role") || "visitor",
    ...eventData,
  });
};

/**
 * Track a user action/interaction
 * @param category - Event category
 * @param action - Event action
 * @param label - Event label (optional)
 * @param value - Event value (optional)
 */
export const trackUserEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
): void => {
  const eventData: UserEvent = {
    event: "user_event",
    event_category: category,
    event_action: action,
  };

  if (label) eventData.event_label = label;
  if (value !== undefined) eventData.event_value = value;

  pushToDataLayer(eventData);
};

/**
 * Track button clicks
 * @param buttonName - Name/identifier of the button
 * @param additionalData - Any additional data
 */
export const trackButtonClick = (
  buttonName: string,
  additionalData?: Record<string, any>,
): void => {
  trackEvent("button_click", {
    button_name: buttonName,
    ...additionalData,
  });
};

/**
 * Track form submissions
 * @param formName - Name/identifier of the form
 * @param additionalData - Any additional data
 */
export const trackFormSubmit = (formName: string, additionalData?: Record<string, any>): void => {
  trackEvent("form_submit", {
    form_name: formName,
    ...additionalData,
  });
};

/**
 * Track search queries
 * @param searchTerm - The search term
 * @param resultsCount - Number of results (optional)
 */
export const trackSearch = (searchTerm: string, resultsCount?: number): void => {
  trackEvent("search", {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

/**
 * Track user login
 * @param method - Login method (e.g., 'email', 'google', 'facebook')
 * @param userId - User ID (optional, be mindful of privacy)
 */
export const trackLogin = (method: string, userId?: string): void => {
  trackEvent("login", {
    method,
    user_id: userId,
  });
};

/**
 * Track user signup/registration
 * @param method - Signup method
 */
export const trackSignup = (method: string): void => {
  trackEvent("sign_up", {
    method,
  });
};

/**
 * Track errors
 * @param errorMessage - Error message
 * @param errorType - Type of error
 * @param fatal - Whether the error is fatal
 */
export const trackError = (
  errorMessage: string,
  errorType?: string,
  fatal: boolean = false,
): void => {
  trackEvent("error", {
    error_message: errorMessage,
    error_type: errorType,
    fatal,
  });
};

/**
 * Set user properties in dataLayer
 * @param userId - User ID
 * @param properties - Additional user properties
 */
export const setUserProperties = (userId: string, properties?: Record<string, any>): void => {
  pushToDataLayer({
    event: "user_properties",
    user_id: userId,
    ...properties,
  });
};

/**
 * Clear/reset the dataLayer
 * Note: This should be used with caution
 */
export const clearDataLayer = (): void => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer = [];
  }
};

/**
 * Video engagement status values aligned with GA4 video tracking conventions.
 * - start: fires once on the first play of a video
 * - play: fires on subsequent plays (after a pause)
 * - pause: fires when the viewer pauses
 * - progress: fires at 25/50/75% playback milestones
 * - complete: fires when the video reaches the end
 */
export type VideoEngagementStatus = "start" | "play" | "pause" | "progress" | "complete";

export interface VideoEngagementParams {
  video_provider: string;
  video_url: string;
  video_title: string;
  video_status: VideoEngagementStatus;
  video_duration: number;
  video_percent: number;
}

/**
 * Track a GA4 `video_engagement` event.
 * Routes through trackEvent so user_role / session_context / region are attached.
 */
export const trackVideoEngagement = (params: VideoEngagementParams): void => {
  trackEvent("video_engagement", {
    video_provider: params.video_provider,
    video_url: params.video_url,
    video_title: params.video_title,
    video_status: params.video_status,
    video_duration: Math.round(params.video_duration) || 0,
    video_percent: Math.max(0, Math.min(100, Math.round(params.video_percent))),
  });
};

export interface MarketingCtaClickParams {
  /** Visible text of the CTA, e.g. "Create Account", "Subscribe" */
  link_text: string;
  /** Optional extra context (e.g. cta_location, destination) */
  [key: string]: any;
}

/**
 * Track a GA4 `marketing_cta_click` event.
 * Fires when a user clicks a key site-wide CTA button (e.g. "Create Account",
 * "Subscribe", "Login"). Routes through trackEvent so user_role /
 * session_context / region are attached automatically.
 * @param linkText - Visible text of the clicked CTA
 * @param additionalData - Optional extra context to attach to the event
 */
export const trackMarketingCtaClick = (
  linkText: string,
  additionalData?: Record<string, any>,
): void => {
  trackEvent("marketing_cta_click", {
    link_text: linkText,
    ...additionalData,
  });
};

export interface CancellationParams {
  /** Plan / subscription tier being cancelled, e.g. "standard", "enhanced" */
  plan_type?: string;
  /** User-provided reason for cancelling the subscription */
  reason: string;
  /** Sales rep / affiliate / partner identifier tied to the subscription, if any */
  rep_id?: string;
  /** Optional extra context (e.g. upgrade_downgrade, from_plan, to_plan) */
  [key: string]: any;
}

/**
 * Track a GA4 `cancellation` event.
 * Fires when a user cancels (unsubscribes from) a provider subscription.
 * Routes through trackEvent so user_role / session_context / region are
 * attached automatically.
 * @param params - Cancellation context (plan_type, reason, rep_id, ...)
 */
export const trackCancellation = (params: CancellationParams): void => {
  trackEvent("cancellation", {
    ...params,
  });
};

/**
 * Track a GA4 `site_search` event.
 * Fires when a user performs a search anywhere on the site (provider search,
 * video search, blog search, unified site search, etc.). Routes through
 * trackEvent so user_role / session_context / region are attached automatically.
 * @param searchTerm - The term the user searched for
 * @param additionalData - Optional extra context (e.g. search_type, results_count)
 */
export const trackSiteSearch = (searchTerm: string, additionalData?: Record<string, any>): void => {
  const term = (searchTerm || "").trim();
  if (!term) return;

  trackEvent("site_search", {
    search_term: term,
    ...additionalData,
  });
};

/**
 * Web Vitals metrics interface
 */
export interface WebVitalsMetrics {
  cls: number; // Cumulative Layout Shift
  lcp_ms: number; // Largest Contentful Paint in milliseconds
  inp_ms: number; // Interaction to Next Paint in milliseconds
  ttfb_ms: number; // Time to First Byte in milliseconds
  url: string; // Current page URL
}

/**
 * Track Web Vitals metrics
 * @param metrics - Web Vitals metrics object
 */
export const trackWebVitals = (metrics: WebVitalsMetrics): void => {
  pushToDataLayer({
    event: "core_web_vitals",
    region: JSON.parse(localStorage.getItem("location") || "{}").region,
    session_context: "web",
    user_role: localStorage.getItem("role") || "visitor",
    cls: metrics.cls,
    lcp_ms: metrics.lcp_ms,
    inp_ms: metrics.inp_ms,
    ttfb_ms: metrics.ttfb_ms,
    url: metrics.url,
  });
};

// Export all functions as default object
export default {
  pushToDataLayer,
  trackPageView,
  trackEvent,
  trackUserEvent,
  trackButtonClick,
  trackFormSubmit,
  trackSearch,
  trackLogin,
  trackSignup,
  trackError,
  setUserProperties,
  clearDataLayer,
  trackWebVitals,
  trackVideoEngagement,
  trackMarketingCtaClick,
  trackCancellation,
  trackSiteSearch,
};
